import React, { useState, useEffect, useCallback } from 'react';
import {
  getProductPrices,
  getProductPriceBreakdown,
  calculateProductPrice,
  saveProductPrice,
  updateChemicalPrice,
} from '../../api/priceCalculation';
import styles from './PriceCalculation.module.scss';
import { Save, RefreshCw } from 'lucide-react';

export default function ProductBasedCalculation({ onError }) {
  const [productList, setProductList] = useState([]);
  const [search, setSearch] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [components, setComponents] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const [updatingChemicalId, setUpdatingChemicalId] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      const data = await getProductPrices();
      setProductList(Array.isArray(data) ? data : data.items || []);
    } catch (e) {
      if (onError) onError(e.message);
    }
  }, [onError]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = search.trim()
    ? productList.filter((p) =>
        (p.product_name || '')
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      )
    : [];

  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setSearch(product.product_name);
    setShowResults(false);
    setBreakdown(null);
    setComponents([]);
    setLoading(true);
    if (onError) onError(null);
    try {
      const data = await getProductPriceBreakdown(product.product_id);
      setBreakdown(data);
      setComponents(
        (data.components || []).map((c) => ({
          chemical_id: c.chemical_id,
          chemical_name: c.chemical_name,
          quantity_g: c.quantity_g != null ? Number(c.quantity_g) : 0,
          quantity_kg: c.quantity_kg != null ? Number(c.quantity_kg) : 0,
          price_per_kg: c.price_per_kg != null ? Number(c.price_per_kg) : 0,
        }))
      );
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateComponent = (chemicalId, field, value) => {
    const num = parseFloat(value);
    const isNum = !isNaN(num) && value !== '';
    setComponents((prev) =>
      prev.map((c) =>
        c.chemical_id === chemicalId
          ? {
              ...c,
              [field]: isNum ? num : value,
              // Always calculate quantity_kg from quantity_g
              quantity_kg: field === 'quantity_g' ? ((isNum ? num : (parseFloat(value) || 0)) / 1000) : c.quantity_kg,
            }
          : c
      )
    );
  };
  const closeSearchResults = () => {
    setTimeout(() => setShowResults(false), 150);
  };


  const totalCost = components.reduce(
    (sum, c) => sum + ((Number(c.quantity_g) || 0) / 1000) * (Number(c.price_per_kg) || 0),
    0
  );
  // Product price per kg = Total Component Cost (sum of all component costs)
  const productPricePerKg = totalCost;

  const handleRecalculateFromServer = async () => {
    if (!selectedProduct) return;
    setLoading(true);
    if (onError) onError(null);
    try {
      const overrides = components
        .filter(
          (c) =>
            c.price_per_kg !== '' &&
            !isNaN(Number(c.price_per_kg)) &&
            Number(c.price_per_kg) >= 0
        )
        .map((c) => ({
          chemical_id: c.chemical_id,
          price_per_kg: Number(c.price_per_kg),
        }));
      const data = await calculateProductPrice(
        selectedProduct.product_id,
        overrides.length ? overrides : null
      );
      setBreakdown(data);
      setComponents(
        (data.components || []).map((c) => ({
          chemical_id: c.chemical_id,
          chemical_name: c.chemical_name,
          quantity_g: c.quantity_g != null ? Number(c.quantity_g) : 0,
          quantity_kg: c.quantity_kg != null ? Number(c.quantity_kg) : 0,
          price_per_kg: c.price_per_kg != null ? Number(c.price_per_kg) : 0,
        }))
      );
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProductPrice = async () => {
    if (!selectedProduct) return;
    setSavingProduct(true);
    if (onError) onError(null);
    try {
      const overrides = components
        .filter(
          (c) =>
            c.price_per_kg !== '' &&
            !isNaN(Number(c.price_per_kg)) &&
            Number(c.price_per_kg) >= 0
        )
        .map((c) => ({
          chemical_id: c.chemical_id,
          price_per_kg: Number(c.price_per_kg),
        }));
      await saveProductPrice(
        selectedProduct.product_id,
        overrides.length ? overrides : null
      );
      if (onError) onError(null);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const handleUpdateChemicalPrice = async (chemicalId) => {
    const comp = components.find((c) => c.chemical_id === chemicalId);
    if (!comp || comp.price_per_kg === '' || isNaN(Number(comp.price_per_kg)))
      return;
    setUpdatingChemicalId(chemicalId);
    if (onError) onError(null);
    try {
      await updateChemicalPrice(chemicalId, {
        price_per_kg: Number(comp.price_per_kg),
      });
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setUpdatingChemicalId(null);
    }
  };

  return (
    <div className={styles.calcSection}>
      <div className={styles.calcSectionTitle}>Search and select a product</div>
      <div className={styles.searchWrapper}>
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Search products…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowResults(e.target.value.trim().length > 0);
            }}
            onFocus={() => {
              if (search.trim().length > 0) {
                setShowResults(true);
              }
            }}
            onBlur={closeSearchResults}
          />
        </div>
        {showResults && (
          <div className={styles.searchResults}>
            {filteredProducts.length === 0 ? (
              <div className={styles.searchResultItem}>No products found</div>
            ) : (
              filteredProducts.slice(0, 20).map((p) => (
                <div
                  key={p.product_id}
                  className={styles.searchResultItem}
                  onClick={() => selectProduct(p)}
                >
                  {p.product_name}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {loading && !breakdown && (
        <div className={styles.loading}>Loading product breakdown…</div>
      )}

      {selectedProduct && breakdown && components.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: 20, fontWeight: 600, color: 'var(--primary-text)' }}>Product Calculation – {breakdown.product_name}</h3>
          <div style={{ marginBottom: 16, padding: 12, background: 'var(--card-bg)', borderRadius: 8, fontSize: 14, border: '1px solid var(--border-color)', color: 'var(--primary-text)' }}>
            <strong>Base Composition:</strong> {breakdown.base_composition_qty} {breakdown.base_composition_unit} ({Number(breakdown.base_composition_kg).toFixed(4)} kg)
          </div>
          <div className={styles.tableWrap} style={{ marginBottom: 16 }}>
            <table className={`${styles.table} ${styles.breakdownTable}`}>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Qty (g)</th>
                  <th>Qty (kg)</th>
                  <th>Price per kg</th>
                  <th>Component Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {components.map((c) => {
                  // Calculate qty_kg from qty_g automatically
                  const qtyKg = (Number(c.quantity_g) || 0) / 1000;
                  const price = Number(c.price_per_kg) || 0;
                  const cost = qtyKg * price;
                  const costDisplay = cost < 0.01 ? cost.toFixed(6) : cost.toFixed(4);
                  return (
                    <tr key={c.chemical_id}>
                      <td style={{ fontWeight: 500, color: 'var(--primary-text)' }}>{c.chemical_name}</td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={c.quantity_g}
                          onChange={(e) =>
                            updateComponent(
                              c.chemical_id,
                              'quantity_g',
                              e.target.value
                            )
                          }
                          style={{ width: 100, padding: '6px 8px', fontSize: 14, background: 'var(--card-bg)', color: 'var(--primary-text)', border: '1px solid var(--border-color)' }}
                        />
                      </td>
                      <td style={{ padding: '8px', color: 'var(--secondary-text)' }}>
                        {qtyKg.toFixed(6)}
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={c.price_per_kg}
                          onChange={(e) =>
                            updateComponent(
                              c.chemical_id,
                              'price_per_kg',
                              e.target.value
                            )
                          }
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val !== '' && (isNaN(parseFloat(val)) || parseFloat(val) < 0)) {
                              updateComponent(c.chemical_id, 'price_per_kg', c.price_per_kg || '');
                            }
                          }}
                          style={{ width: 120, padding: '6px 8px', fontSize: 14, background: 'var(--card-bg)', color: 'var(--primary-text)', border: '1px solid var(--border-color)' }}
                        />
                      </td>
                      <td style={{ fontWeight: 500, color: 'var(--primary-text)' }}>{costDisplay}</td>
                      <td>
                        <button
                          type="button"
                          className={styles.btnSecondary}
                          onClick={() =>
                            handleUpdateChemicalPrice(c.chemical_id)
                          }
                          disabled={
                            updatingChemicalId === c.chemical_id ||
                            c.price_per_kg === '' ||
                            isNaN(Number(c.price_per_kg))
                          }
                          style={{ fontSize: 12, padding: '4px 8px' }}
                        >
                          {updatingChemicalId === c.chemical_id
                            ? 'Updating…'
                            : 'Update'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.totalRow}>
                  <td colSpan={4}><strong>Total Component Cost</strong></td>
                  <td><strong>{totalCost.toFixed(4)}</strong></td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ 
            padding: 16, 
            background: 'var(--card-bg)', 
            borderRadius: 8, 
            border: '1px solid var(--border-color)',
            marginTop: 8,
            marginBottom: 16
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--secondary-text)', marginBottom: 4 }}>Total Component Cost</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--primary-text)' }}>{totalCost.toFixed(4)}</div>
              </div>
              <div style={{ fontSize: 20, color: 'var(--secondary-text)' }}>=</div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--secondary-text)', marginBottom: 4 }}>Product Price per kg</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent-color, #2563eb)' }}>
                  {productPricePerKg.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={handleRecalculateFromServer}
              disabled={loading}
            >
              <RefreshCw size={14} /> Recalculate from server
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleSaveProductPrice}
              disabled={savingProduct}
            >
              <Save size={14} /> {savingProduct ? 'Saving…' : 'Save as product price'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
